import React, { useState, useEffect } from "react";
import { AiFillHeart, AiOutlineHeart } from "react-icons/ai";
import { IoIosAddCircle } from "react-icons/io";
import { MdRemoveCircle } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { addProductToCart, updateCartItemQuantity, removeProductFromCart } from "../redux/cartSlice";
import { addProductToWishlist, removeProductFromWishlist } from "../redux/WishlistSlice";
import { getFallbackImage } from "../utils/imageLoader";
import { getFullImageUrl } from "../api";
import ProductModal from "./ProductModal";
import styles from "./Card.module.css";
import { toast } from 'react-hot-toast';

// Using forwardRef to allow parent components to access the card's DOM node
const Card = React.forwardRef(({ post, isWishlistPage = false, onClick }, ref) => {
  const [modalOpen, setModalOpen] = useState(false);
  // track the current image index for slider
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const dispatch = useDispatch();
  
  // Access wishlist and cart items from the Redux store
  const wishlistItems = useSelector(state => state.wishlist.items);
  const cartItems = useSelector(state => state.cart.items);
  
  // checks if the product is in the wishlist and cart
  const wishlistEntry = post ? wishlistItems.find(item => item.id === post._id) : null;
  const isInWishlist = !!wishlistEntry;
  const currentItem = post ? cartItems.find(item => item.id === post._id) : null;
  const quantity = currentItem ? currentItem.quantity : 0;
  
  // Prepare image URLs
  const images = post?.images?.length > 0 ? post.images.map(getFullImageUrl) : [];
  const hasMultipleImages = images.length > 1;

  // Auto-rotate images
  useEffect(() => {
    if (hasMultipleImages) {
      const interval = setInterval(() => {
        setCurrentImageIndex(prev => (prev + 1) % images.length);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [hasMultipleImages, images.length]);

  const price = post?.variants?.[0]?.price || 0;

  // Add to cart handler
  const handleAddToCart = (e) => {
    e.stopPropagation();
    if (!post?.variants?.length) return;
    const firstVariant = post.variants[0];
    dispatch(addProductToCart({
      ...post,
      id: post._id,
      price: firstVariant.price,
      image: images[0] || getFallbackImage(),
      title: post.name,
      variantId: firstVariant._id,
      selectedColor: firstVariant.color,
      selectedSize: firstVariant.size
    }, 1));
  };

  // handle increment and decrement quantity
  const handleIncrement = (e) => {
    e.stopPropagation();
    if (currentItem) {
      // Get current variant stock
      const firstVariant = post?.variants?.[0];
      const availableStock = firstVariant?.stock || 0;
      
      // Check if incrementing would exceed stock
      if (quantity >= availableStock) {
        toast(`⚠️ Stock Limit Reached!\nOnly ${availableStock} items available`, {
          icon: '📦',
          style: {
            background: '#fff3cd',
            color: '#856404',
            border: '2px solid #ffc107',
            fontWeight: '600'
          },
          duration: 3000
        });
        return;
      }
      
      dispatch(updateCartItemQuantity(currentItem, quantity + 1));
    }
  };

  const handleDecrement = (e) => {
    e.stopPropagation();
    if (currentItem) {
      if (quantity === 1) { dispatch(removeProductFromCart(currentItem)); } 
      else { dispatch(updateCartItemQuantity(currentItem, quantity - 1)); }
    }
  };

  return (
    <>
      <div className={styles.card} ref={ref}>
        <div className={styles.mirrorFrame} onClick={() => setModalOpen(true)}>
          <div className={styles.mirrorGlass}>
            {hasMultipleImages ? (
              <div
                className={styles.imageSlider}
                style={{ transform: `translateX(-${currentImageIndex * 100}%)` }}
              >
                {images.map((imgUrl, idx) => (
                  <div key={idx} className={styles.imageSlide}>
                    <img src={imgUrl} alt={post.name} className={styles.image} />
                  </div>
                ))}
              </div>
            ) : (
              <img src={images[0] || getFallbackImage()} alt={post.name} className={styles.image} />
            )}

            <button
              className={styles.wishlistButton}
              onClick={(e) => {
                e.stopPropagation();
                if (isInWishlist) {
                  // Pass the existing wishlist entry so thunk has id/wishlistId
                  dispatch(removeProductFromWishlist(wishlistEntry));
                } else {
                  dispatch(
                    addProductToWishlist({
                      ...post,
                      id: post._id,
                      image: images[0] || getFallbackImage(),
                      title: post.name,
                      price
                    })
                  );
                }
              }}
            >
              {isInWishlist ? (
                <AiFillHeart className={`${styles.wishlistIcon} ${styles.filled}`} />
              ) : (
                <AiOutlineHeart className={`${styles.wishlistIcon} ${styles.outline}`} />
              )}
            </button>
          </div>
        </div>

        <div className={styles.standSection}>
          <p className={styles.title} onClick={() => setModalOpen(true)}>{post.name}</p>
          <p className={styles.price}>₹{price.toFixed(2)}</p>

          {!isWishlistPage && (
            <div className={styles.actions}>
              {quantity === 0 ? (
                <button className={styles.button} onClick={handleAddToCart}>Add to Cart</button>
              ) : (
                <div className={styles.quantityContainer}>
                  <button className={styles.quantityButton} onClick={handleDecrement}><MdRemoveCircle /></button>
                  <span className={styles.quantity}>{quantity}</span>
                  <button className={styles.quantityButton} onClick={handleIncrement}><IoIosAddCircle /></button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <ProductModal post={post} isOpen={modalOpen} onClose={() => setModalOpen(false)} />
    </>
  );
});

export default Card;